import React, { useMemo, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"

import Checkbox from "../../../shared/components/fields/checkbox"
import { CloudflarePlayer } from "../../../react-bricks/components/video"
import IconAction from "../../../shared/components/universal/icon-action"
import PhoneInput from "../../../shared/components/fields/phone"
import { WhatsappTokenLinkBody } from "../../../shared/sdk/types"
import { countries } from "../../../shared/config/countries"
import { errorToast } from "../../../shared/utils/error-toast"
import { toast } from "react-toastify"
import { useLocalesState } from "../../../shared/context/locales"
import { useMutation } from "@tanstack/react-query"
import { useQueryParams } from "../../../shared/hooks/use-url-search-params"
import { useSDK } from "../../../shared/context/client"
import { useTranslation } from "react-i18next"

type FormValues = {
    cc: string
    number: string
    checkbox: boolean
}

const AVAILABLE_COUNTRIES = ["DE", "AT", "CH"]

const Whatsapp = () => {
    const { t } = useTranslation("translations")

    const { region } = useLocalesState()

    const [number, setNumber] = useState<string>()

    const defaultCountry = useMemo(() => countries.find(c => c.countryCode === region), [region])

    const countryCodeOptions = useMemo(() => {
        const options = countries
            .filter(c => (AVAILABLE_COUNTRIES ? AVAILABLE_COUNTRIES.some(a => a === c.countryCode) : true))
            .filter(c => c.countryCode !== region)
            .sort((a, b) => a.callingCode.localeCompare(b.callingCode))
        if (defaultCountry) {
            options.unshift(defaultCountry)
        }
        return options
    }, [region, defaultCountry])

    const {
        formState: { errors },
        handleSubmit,
        register,
        reset,
        control,
        watch,
    } = useForm<FormValues>({
        mode: "onTouched",
        defaultValues: {
            cc: countryCodeOptions[0].callingCode,
            number: "",
            checkbox: false,
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const watchNumber = watch("number")
    const watchCheckbox = watch("checkbox")

    const { getQueryParam } = useQueryParams()
    const token = getQueryParam("token")

    const client = useSDK()

    const mutate = useMutation({
        mutationFn: (body: WhatsappTokenLinkBody) => client.postWhatsappTokenLink(token, body),
    })

    const submit: SubmitHandler<FormValues> = async values => {
        const body = {
            mobile: values.cc + values.number,
        }

        try {
            const response = await mutate.mutateAsync(body)
            reset()
            toast.dismiss()
            setNumber(response)
        } catch (error) {
            errorToast(error)
        }
    }

    return (
        <div className="mx-auto max-w-[550px]">
            <h1 className="mb-4 text-center font-gibson text-xl font-semibold sm:mb-5 sm:text-3xl/3xl lg:text-5xl/5xl">
                {t("whatsapp_form_headline")}
            </h1>
            <p className="mb-7 text-center text-md sm:mb-10 sm:text-lg lg:text-xl">{t("whatsapp_form_subheadline")}</p>
            {!number ? (
                <form onSubmit={handleSubmit(submit)} className="rounded-base bg-white p-7 pb-0 sm:px-20 sm:pt-10">
                    <p className="mb-3.5 font-gibson text-md font-semibold sm:mb-5 sm:text-lg lg:text-xl">
                        {t("whatsapp_form_phone_label")} <span className="text-ui-red">*</span>
                    </p>
                    <PhoneInput
                        control={control}
                        options={countryCodeOptions.map(c => ({
                            value: c.callingCode,
                            name: t(c.name),
                            cc: c.callingCode,
                            ornament: c.flag,
                        }))}
                        rules={{
                            required: { value: true, message: t("form_error_required") },
                            validate: {
                                noLeadingZero: value => {
                                    if (typeof value === "string") {
                                        return /^[0]/.test(value) ? t("form_error_zero") : true
                                    } else {
                                        return t("form_error_pattern")
                                    }
                                },
                                onlyNumbers: value => {
                                    if (typeof value === "string") {
                                        return /^[\d\s]+$/.test(value) ? true : t("form_error_numbers_only")
                                    } else {
                                        return t("form_error_pattern")
                                    }
                                },
                                lengthCheck: value => {
                                    if (typeof value === "string") {
                                        return value.length >= 4 && value.length <= 12 ? true : t("form_error_length")
                                    } else {
                                        return t("form_error_pattern")
                                    }
                                },
                            },
                        }}
                        type="tel"
                        nameCC="cc"
                        nameNumber="number"
                        required
                        placeholder="170 123 456 78"
                    />
                    <Checkbox
                        register={register("checkbox", {
                            required: { value: true, message: t("form_error_required") },
                        })}
                        error={errors}
                        label={t("whatsapp_form_checkbox_label")}
                        required
                    />
                    <p className="mb-7 text-xs sm:text-sm">
                        <span className="text-ui-red">*</span> {t("form_required_field")}
                    </p>
                    <div className="flex justify-center">
                        <IconAction
                            title={t("whatsapp_form_submit")}
                            button
                            submit
                            disabled={Object.keys(errors).length > 0 || !watchNumber || watchCheckbox === false}
                        />
                    </div>
                    <div className="mx-auto mt-10 h-[192px] w-[214px] child:h-full child:w-full xs:h-[216px] xs:w-[240px] sm:h-[270px] sm:w-[300px]">
                        <CloudflarePlayer id="f809d37bb222250acfe6a49af269efe6" autoplay />
                    </div>
                </form>
            ) : (
                <div className="flex flex-col items-center justify-center rounded-base bg-white p-7 text-center sm:px-20 sm:py-10">
                    <div className="mb-5 flex h-[50px] w-[50px] items-center justify-center rounded-full bg-swamp-green sm:h-15 sm:w-15 lg:h-[70px] lg:w-[70px]">
                        <i className="lavita-icon text-lg leading-none text-white sm:text-xl"></i>
                    </div>
                    <h2 className="mb-7 font-gibson text-lg font-semibold sm:mb-10 sm:text-xl lg:text-2xl">
                        {t("whatsapp_form_success_headline")}
                    </h2>
                    <p className="mb-3.5 text-base lg:text-md">{t("whatsapp_form_success_subheadline")}</p>
                    <div className="mb-10 w-full rounded-full bg-background-tide p-5 text-md font-semibold sm:text-lg lg:p-6 lg:text-xl">
                        {number}
                    </div>
                    <p className="mb-7 text-base lg:text-md">{t("whatsapp_form_success_message")}</p>
                    <a
                        className="mb-5 flex items-center justify-center gap-2.5 rounded-full border border-solid border-border-tide px-7 py-3.5 text-blue"
                        href={`tel:${t("whatsapp_form_success_phone_url")}`}
                    >
                        <i className="lavita-icon text-base leading-none sm:text-md"></i>
                        <span className="text-base font-semibold underline sm:text-md">
                            {t("whatsapp_form_success_phone_number")}
                        </span>
                    </a>
                    <a
                        className="flex items-center justify-center gap-2.5 rounded-full border border-solid border-border-tide px-7 py-3.5 text-blue"
                        href={`mailto:${t("whatsapp_form_success_email")}`}
                    >
                        <i className="lavita-icon text-base leading-none sm:text-md"></i>
                        <span className="text-base font-semibold underline sm:text-md">
                            {t("whatsapp_form_success_email")}
                        </span>
                    </a>
                </div>
            )}
        </div>
    )
}

export default Whatsapp
